'use client';

/**
 * AmbientWing — the aquarium
 *
 * A site-wide ambient layer that lives between the GlobalBackground (deep teal
 * + cursor mint spotlight) and the page content. The metaphor is a fish in an
 * aquarium: the math equation paints a wing-like creature that swims slowly
 * around the page, attracted to the visitor's cursor but never tethered.
 * Behind frosted glass.
 *
 * Three things make it feel alive instead of mechanical:
 *
 *   1. WAYPOINT MOTION  — the fish has poses (position, size, tilt) it glides
 *                          between. Each leg is 4-9 seconds, cubic-eased
 *                          in-and-out. No linear pans. When idle the
 *                          waypoints scatter across the safe zone; when the
 *                          cursor is on screen, 60% of each new waypoint
 *                          biases toward it.
 *
 *   2. SIZE AS DEPTH    — fish.scale varies between ~0.6× and ~1.3×. Bigger =
 *                          "closer to the glass" = more visible. Smaller =
 *                          "deeper in the tank" = subtler. Cubic-eased too.
 *
 *   3. VARIABLE PRESENCE — visibility isn't gated by variable blur (a CSS
 *                          backdrop-filter trick that compounds badly across
 *                          stacked panes). Instead the wing's per-frame trail
 *                          decay is moderate, so brightness CONCENTRATES at
 *                          the fish's current position and fades behind it.
 *                          Some places naturally show more than others —
 *                          wherever the fish is now is brightest; its wake
 *                          dims out behind it.
 *
 * Plus the original "by distance" reactions:
 *   - Cursor proximity → boosts the wing's flap rate (math t advances faster)
 *   - Cursor near a dot → mint tint (#7CFFB2 — Datacode signal colour)
 *   - Scroll velocity   → transient t-kick that decays in ~0.7s
 *   - Reduced motion    → fish freezes mid-pose, no animation
 *
 * Performance: ~5,000 dot iterations per frame (STRIDE 2 across yuruyurau's
 * native 10,000 range). Pauses when tab hidden or off-viewport.
 *
 * Math note: the per-dot equation is yuruyurau's May 7 2026 wing, used here
 * behind heavy blur where the specific equation isn't visually recognisable.
 * Swap for an original equation when shipping.
 */

import { useEffect, useRef, useState } from 'react';

// ── wing math controls ───────────────────────────────────────────
const STRIDE = 4;                 // 2 → 4 halves per-frame dot count (5000 → 2500)
const I_MAX = 10000;
const TRAIL_FADE = 0.06;          // destination-out alpha per frame
const BASE_T_RATE = Math.PI * 0.6; // base wing-flap rate (rad/sec)
const PROXIMITY_BOOST_MAX = 0.7;   // cursor proximity multiplies t-rate up to ×(1 + this)
const PROXIMITY_RADIUS_PX = 380;   // cursor distance over which proximity ramps
const MINT_TINT_RADIUS_PX = 140;
const SCROLL_T_GAIN = 0.0015;
const SCROLL_DECAY = 0.92;

// ── fish behaviour controls ──────────────────────────────────────
const SEGMENT_MIN_SEC = 4.5;
const SEGMENT_MAX_SEC = 8.5;
const CURSOR_BIAS = 0.55;          // weight new waypoint toward cursor (0..1)
const SCALE_MIN = 0.55;
const SCALE_MAX = 1.35;
const ROTATION_RANGE = 0.34;       // ± radians at extreme waypoints
const SAFE_MARGIN_FRAC = 0.16;     // keep waypoints this far from viewport edges
const SPEED_TO_TRATE = 0.0009;     // additional t-rate per (px/sec of fish speed)
// Subtle low-frequency water current — adds organic sway on top of the path.
const CURRENT_AMPLITUDE_PX = 9;

// ── presence (overall visibility, drifts on a long timescale) ────
// Two out-of-phase sines avoid a predictable pulse: ~37s and ~20s periods,
// summed at unequal weights. PRESENCE_MIN keeps the fish from disappearing —
// held high: the fish should always read as *there*, just deeper or closer.
const PRESENCE_MIN = 0.62;
const PRESENCE_MAX = 1.0;
const PRESENCE_FREQ_A = 0.00017;
const PRESENCE_FREQ_B = 0.00031;

// ── spatial clarity field (clearer/cloudier patches in the frosted
// glass). Per-dot brightness samples this and falls into one of three
// buckets. Drifts slowly so the clear patches wander. ────────────────
const PATCH_FREQ_X = 0.0028;
const PATCH_FREQ_Y = 0.0033;
const PATCH_DRIFT = 0.00012;

// ── wandering color ──────────────────────────────────────────────
// The wing's dot color drifts through random hues over time. Lightness is
// pinned high (bright pastel territory) so a new color never lands dark and
// disappears against the deep-teal background. Each color holds for a few
// seconds, then eases into the next one.
const COLOR_HOLD_MIN_SEC = 5;
const COLOR_HOLD_MAX_SEC = 11;
const COLOR_LERP_RATE = 0.45;      // per-second approach toward the target color
const COLOR_SAT_MIN = 0.55;
const COLOR_SAT_MAX = 0.9;
const COLOR_LIGHT_MIN = 0.62;
const COLOR_LIGHT_MAX = 0.82;

// ── intro / loading sequence ─────────────────────────────────────
// On a hard page load the wing IS the loader: a full-screen teal cover sits
// above the page and the creature swims nearly sharp, front and center.
// When the page has loaded (and at least INTRO_MIN_MS has passed) it
// "dives" — blur ramps up while wing and cover dissolve together — and it
// re-emerges faintly as the ambient background layer. Client-side
// navigations never remount this component, so the intro plays only on
// hard loads.
const INTRO_MIN_MS = 1600;        // loader always shows at least this long
const INTRO_MAX_WAIT_MS = 4000;   // merge even if window load hasn't fired
const MERGE_MS = 1100;            // dive-into-the-glass transition
const AMBIENT_FADE_MS = 900;      // faint re-emerge behind content
const INTRO_SEG_MIN_SEC = 1.8;    // livelier waypoint pace while loading
const INTRO_SEG_MAX_SEC = 3.2;

type Phase = 'intro' | 'merge' | 'ambient' | 'off';

// ── visual ───────────────────────────────────────────────────────
const WING_DIAMETER_FRAC = 0.42;   // wing size at fish.scale = 1, as fraction of min viewport
const NATIVE = 400;
const NATIVE_Y_OFFSET = 100;       // shift wing-local y so the wing centres on (0, 0)

interface Pose { x: number; y: number; scale: number; rot: number; }

function cubicInOut(t: number) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function clamp(v: number, a: number, b: number) {
  return v < a ? a : v > b ? b : v;
}

function hslToRgb(h: number, s: number, l: number) {
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    return Math.round(255 * (l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))));
  };
  return { r: f(0), g: f(8), b: f(4) };
}

export function AmbientWing({ enabled = true }: { enabled?: boolean } = {}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sharpCanvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // 'intro' is the SSR/first-paint state so the teal cover is in the static
  // HTML from the very first byte — no flash of unstyled page underneath.
  const [phase, setPhase] = useState<Phase>('intro');
  const phaseRef = useRef<Phase>('intro');

  useEffect(() => {
    if (!enabled) return;
    // Mobile + low-power devices don't get the ALWAYS-ON ambient layer (a
    // full-screen canvas with `lighter` composite and a 22px CSS blur is a
    // known INP killer on phones) — but they DO get the short loading intro;
    // after the merge the loop is torn down entirely.
    const isCoarse = window.matchMedia?.('(pointer: coarse)').matches;
    // innerWidth can transiently read 0 in backgrounded/pre-layout tabs —
    // only treat a real, positive measurement as "narrow".
    const isNarrow = window.innerWidth > 0 && window.innerWidth < 768;
    const lowDeviceMemory =
      typeof (navigator as Navigator & { deviceMemory?: number }).deviceMemory === 'number' &&
      (navigator as Navigator & { deviceMemory?: number }).deviceMemory! < 4;
    const skipAmbient = isCoarse || isNarrow || lowDeviceMemory;
    const canvas = canvasRef.current;
    const sharpCanvas = sharpCanvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ctxOrNull = canvas.getContext('2d', { alpha: true });
    if (!ctxOrNull) return;
    const ctx: CanvasRenderingContext2D = ctxOrNull;
    // The sharp overlay shares the main canvas's pixel data via drawImage.
    // We render the wing math once into `canvas`; this is its mirror at a
    // lower blur radius, masked to a drifting "clear patch" so the fish
    // reads sharper there. If for any reason this context can't be obtained
    // we fall back to single-layer rendering.
    const sharpCtx = sharpCanvas?.getContext('2d', { alpha: true }) ?? null;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let viewportW = window.innerWidth;
    let viewportH = window.innerHeight;
    // Cap dpr at 1.0: the 22px CSS blur on the canvas destroys retina detail
    // anyway, so paying for 2× pixels just doubles paint cost for no benefit.
    let dpr = 1;
    let visible = true;
    let docVisible = !document.hidden;
    let rafId = 0;

    // Cursor in screen space. -9999 sentinel = "off canvas".
    const cursor = { x: -9999, y: -9999 };
    let scrollLastY = window.scrollY;
    let scrollKick = 0;
    let lastFrameMs = performance.now();
    let tAccumulator = 0;
    // Smoothed fish speed (for wing-flap-rate coupling). Low-passed so a
    // sudden waypoint switch doesn't whip the flap rate.
    let smoothedSpeed = 0;

    // ── fish state machine ──
    // path[] is a queue of 4 waypoints. We interpolate between path[1] and
    // path[2] using path[0] and path[3] as tangent neighbours (Catmull-Rom).
    // This gives a continuous curved trajectory — no deceleration at every
    // waypoint, which is what made the old single-leg motion feel mechanical.
    const fish: {
      pose: Pose;
      path: Pose[];          // length 4 once seeded
      segStart: number;      // ms timestamp for path[1]→path[2]
      segDuration: number;   // seconds
    } = {
      pose: { x: 0, y: 0, scale: 1, rot: 0 },
      path: [],
      segStart: 0,
      segDuration: 6,
    };

    function randomInSafe(): { x: number; y: number } {
      const mx = viewportW * SAFE_MARGIN_FRAC;
      const my = viewportH * SAFE_MARGIN_FRAC;
      return {
        x: mx + Math.random() * (viewportW - 2 * mx),
        y: my + Math.random() * (viewportH - 2 * my),
      };
    }

    function segmentDuration(): number {
      // Livelier pace while the wing is the loader; lazy drift once ambient.
      return phaseRef.current === 'ambient'
        ? SEGMENT_MIN_SEC + Math.random() * (SEGMENT_MAX_SEC - SEGMENT_MIN_SEC)
        : INTRO_SEG_MIN_SEC + Math.random() * (INTRO_SEG_MAX_SEC - INTRO_SEG_MIN_SEC);
    }

    function generateWaypoint(prevX: number, prevY: number): Pose {
      // During the loading intro the creature circles the viewport centre —
      // it's the focal point of the loader, not a wanderer.
      if (phaseRef.current !== 'ambient') {
        const x = viewportW * (0.5 + (Math.random() - 0.5) * 0.3);
        const y = viewportH * (0.42 + (Math.random() - 0.5) * 0.28);
        const scale = 0.95 + Math.random() * 0.3;
        const heading = Math.atan2(y - prevY, x - prevX);
        const rot = Math.sin(heading) * ROTATION_RANGE * 0.6 + (Math.random() - 0.5) * 0.18;
        return { x, y, scale, rot };
      }
      const wander = randomInSafe();
      let x = wander.x, y = wander.y;
      // Bias toward cursor if it's on screen.
      if (cursor.x >= 0) {
        x = wander.x * (1 - CURSOR_BIAS) + cursor.x * CURSOR_BIAS;
        y = wander.y * (1 - CURSOR_BIAS) + cursor.y * CURSOR_BIAS;
        // Clamp to safe zone so the fish never beelines off-canvas.
        x = clamp(x, viewportW * SAFE_MARGIN_FRAC, viewportW * (1 - SAFE_MARGIN_FRAC));
        y = clamp(y, viewportH * SAFE_MARGIN_FRAC, viewportH * (1 - SAFE_MARGIN_FRAC));
      }
      const scale = SCALE_MIN + Math.random() * (SCALE_MAX - SCALE_MIN);
      // Heading-based tilt against the previous waypoint, so the fish faces
      // where it's going through this point.
      const dx = x - prevX, dy = y - prevY;
      const heading = Math.atan2(dy, dx);
      const rot = Math.sin(heading) * ROTATION_RANGE * 0.6 + (Math.random() - 0.5) * 0.18;
      return { x, y, scale, rot };
    }

    function seedPath(now: number) {
      // Path needs 4 entries: [prev, current, next, after]. We start with
      // pose as both [0] and [1] (degenerate tangent at the boundary is fine
      // — Catmull-Rom just won't overshoot there).
      const p0: Pose = { ...fish.pose };
      const p1: Pose = { ...fish.pose };
      const p2 = generateWaypoint(p1.x, p1.y);
      const p3 = generateWaypoint(p2.x, p2.y);
      fish.path = [p0, p1, p2, p3];
      fish.segStart = now;
      fish.segDuration = segmentDuration();
    }

    function advanceSegment(now: number) {
      // Slide the window forward: drop the oldest, append a fresh waypoint.
      fish.path.shift();
      const last = fish.path[fish.path.length - 1];
      fish.path.push(generateWaypoint(last.x, last.y));
      fish.segStart = now;
      fish.segDuration = segmentDuration();
    }

    // Catmull-Rom sample. `u` is 0..1 across path[1] → path[2], using
    // path[0] and path[3] for continuous tangent at the endpoints.
    function sampleCR(u: number): Pose {
      const p0 = fish.path[0], p1 = fish.path[1], p2 = fish.path[2], p3 = fish.path[3];
      const u2 = u * u, u3 = u2 * u;
      const f0 = -0.5 * u3 + u2 - 0.5 * u;
      const f1 = 1.5 * u3 - 2.5 * u2 + 1;
      const f2 = -1.5 * u3 + 2 * u2 + 0.5 * u;
      const f3 = 0.5 * u3 - 0.5 * u2;
      return {
        x:     f0 * p0.x     + f1 * p1.x     + f2 * p2.x     + f3 * p3.x,
        y:     f0 * p0.y     + f1 * p1.y     + f2 * p2.y     + f3 * p3.y,
        scale: f0 * p0.scale + f1 * p1.scale + f2 * p2.scale + f3 * p3.scale,
        rot:   f0 * p0.rot   + f1 * p1.rot   + f2 * p2.rot   + f3 * p3.rot,
      };
    }

    // ── viewport / events ──
    const resize = () => {
      viewportW = window.innerWidth;
      viewportH = window.innerHeight;
      dpr = 1;
      canvas.width = Math.floor(viewportW * dpr);
      canvas.height = Math.floor(viewportH * dpr);
      canvas.style.width = `${viewportW}px`;
      canvas.style.height = `${viewportH}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, viewportW, viewportH);
      if (sharpCanvas && sharpCtx) {
        sharpCanvas.width = canvas.width;
        sharpCanvas.height = canvas.height;
        sharpCanvas.style.width = `${viewportW}px`;
        sharpCanvas.style.height = `${viewportH}px`;
        sharpCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
        sharpCtx.clearRect(0, 0, viewportW, viewportH);
      }
      // Seed fish at viewport centre if it has no real pose yet. During the
      // loading intro it starts dead-center (it's the loader); ambient-only
      // starts (reduced motion) keep the old off-center seed.
      if (fish.pose.x === 0 && fish.pose.y === 0) {
        const centered = phaseRef.current !== 'ambient';
        fish.pose = {
          x: viewportW * (centered ? 0.5 : 0.62),
          y: viewportH * (centered ? 0.44 : 0.46),
          scale: centered ? 1.1 : 1,
          rot: 0,
        };
        seedPath(performance.now());
      }
    };

    const onMouseMove = (e: MouseEvent) => {
      cursor.x = e.clientX;
      cursor.y = e.clientY;
    };
    const onMouseLeave = () => {
      cursor.x = -9999;
      cursor.y = -9999;
    };
    const onScroll = () => {
      const cur = window.scrollY;
      const delta = cur - scrollLastY;
      scrollLastY = cur;
      scrollKick += Math.abs(delta) * SCROLL_T_GAIN;
    };
    const onDocVisibility = () => {
      docVisible = !document.hidden;
    };

    // ── light-section awareness ──
    // The wing sits BEHIND page content, but sections with translucent light
    // backgrounds still let it show through, so it adapts: white additive
    // dots over dark sections, deep teal ink over light ones.
    // Document-space ranges of every `.light-section` are cached (measured at
    // init, on resize, and on a slow interval so client-side navigations and
    // late layout shifts are picked up) and the fish's document Y is tested
    // against them each frame — no layout reads in the hot path.
    let lightRanges: Array<{ top: number; bottom: number }> = [];
    const measureLightSections = () => {
      lightRanges = Array.from(document.querySelectorAll('.light-section')).map((el) => {
        const r = el.getBoundingClientRect();
        const top = r.top + window.scrollY;
        return { top, bottom: top + r.height };
      });
    };
    // 0 = over dark, 1 = over light. Eased per frame so the fish gently
    // fades through the crossover instead of popping colors.
    let lightMix = 0;

    // ── render ──
    const mintRGB = { r: 124, g: 255, b: 178 }; // #7CFFB2 (dark sections)
    const inkAccentRGB = { r: 13, g: 118, b: 82 }; // deep accent (light sections)
    const inkRGB = { r: 23, g: 56, b: 53 }; // brand teal ink (light sections)

    // Wandering dot color (dark sections only — light sections stay on ink
    // for contrast). Starts on a random bright hue and drifts to a fresh one
    // every few seconds.
    const randomBrightColor = () =>
      hslToRgb(
        Math.random() * 360,
        COLOR_SAT_MIN + Math.random() * (COLOR_SAT_MAX - COLOR_SAT_MIN),
        COLOR_LIGHT_MIN + Math.random() * (COLOR_LIGHT_MAX - COLOR_LIGHT_MIN),
      );
    const wingColor = randomBrightColor();
    let colorTarget = randomBrightColor();
    let nextColorSwitch =
      performance.now() +
      (COLOR_HOLD_MIN_SEC + Math.random() * (COLOR_HOLD_MAX_SEC - COLOR_HOLD_MIN_SEC)) * 1000;

    // Pre-allocated dot buckets. We bucket each dot into one of three
    // "clarity" levels (dim/mid/bright) based on a slow drifting 2D wave at
    // the dot's screen position — that makes some patches of the wing read
    // as visible-through-the-glass and others as buried. Three batched
    // fills total (one per bucket) keeps draw cost low.
    type Dot = { x: number; y: number; r: number };
    const bucketDim:    Dot[] = [];
    const bucketMid:    Dot[] = [];
    const bucketBright: Dot[] = [];
    const mintArcs:     Dot[] = [];

    function paintField(mathT: number, proximity: number, presence: number, now: number) {
      // Trail decay — destination-out erases a thin slice of alpha each frame
      // so the wing concentrates at the current position and fades in its wake.
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = `rgba(0,0,0,${TRAIL_FADE})`;
      ctx.fillRect(0, 0, viewportW, viewportH);

      // Color mode: over dark sections white dots draw additively ('lighter'
      // pops against black); over light sections that would be invisible, so
      // the fish switches to brand-teal ink with normal compositing. Presence
      // dips to zero at the crossover (crossFade) so the switch reads as the
      // fish diving through the boundary, not a color pop.
      const isLight = lightMix >= 0.5;
      const crossFade = Math.min(1, Math.abs(lightMix - 0.5) * 2);
      presence *= crossFade;
      // Ink on light backgrounds reads perceptually stronger than white glow
      // on dark — pull it further back so both modes feel equally ambient.
      if (isLight) presence *= 0.6;
      const dotRGB = isLight
        ? inkRGB
        : { r: Math.round(wingColor.r), g: Math.round(wingColor.g), b: Math.round(wingColor.b) };
      const tintRGB = isLight ? inkAccentRGB : mintRGB;
      ctx.globalCompositeOperation = isLight ? 'source-over' : 'lighter';

      // Cull/transform setup
      const cosR = Math.cos(fish.pose.rot);
      const sinR = Math.sin(fish.pose.rot);
      const minDim = Math.min(viewportW, viewportH);
      const baseScale = (minDim * WING_DIAMETER_FRAC) / NATIVE;
      const totalScale = baseScale * fish.pose.scale;
      const tx = fish.pose.x;
      const ty = fish.pose.y;
      const cursorOn = cursor.x >= 0;
      const mintR2 = MINT_TINT_RADIUS_PX * MINT_TINT_RADIUS_PX;

      bucketDim.length = 0;
      bucketMid.length = 0;
      bucketBright.length = 0;
      mintArcs.length = 0;

      const patchDriftT = now * PATCH_DRIFT;

      for (let i = I_MAX; i > 0; i -= STRIDE) {
        const x = i;
        const y = i / 235;
        const k = 4 * Math.cos(x / 21);
        const e = y / 8 - 20;
        const d = Math.hypot(k, e);
        const q = 3 * Math.sin(k * 2) + 0.3 / k +
                  Math.sin(y / 19) * k * (9 + 2 * Math.sin(e * 14 - d * 3 + mathT * 2));
        const c = d - mathT;
        // Wing-local (centred on 0, 0). NATIVE/2 = 200, NATIVE_Y_OFFSET = 100.
        const nx = q + 50 * Math.cos(c) + 200 - NATIVE / 2;
        const ny = q * Math.sin(c) + d * 39 - 475 + NATIVE_Y_OFFSET - NATIVE / 2;
        // Fish transform: rotate then scale then translate into screen space.
        const px = tx + totalScale * (cosR * nx - sinR * ny);
        const py = ty + totalScale * (sinR * nx + cosR * ny);
        if (px < -40 || px > viewportW + 40 || py < -40 || py > viewportH + 40) continue;
        const r = (k * k > 15 ? 2 : 1) * totalScale * 1.1;

        if (cursorOn) {
          const ddx = px - cursor.x;
          const ddy = py - cursor.y;
          if (ddx * ddx + ddy * ddy < mintR2) {
            mintArcs.push({ x: px, y: py, r });
            continue;
          }
        }

        // Spatial clarity field — sum of three slow sines at the dot's
        // screen position. Output ~0..1, drifts in time. Cheap and looks
        // organic because the three terms have different frequencies.
        const patch =
          0.5 + 0.5 * (
            Math.sin(px * PATCH_FREQ_X + patchDriftT)         * 0.40 +
            Math.sin(py * PATCH_FREQ_Y + patchDriftT * 1.3 + 1.7) * 0.40 +
            Math.sin((px + py) * PATCH_FREQ_X * 0.6 + patchDriftT * 0.8 + 0.3) * 0.20
          );

        const dot = { x: px, y: py, r };
        if (patch > 0.66) bucketBright.push(dot);
        else if (patch > 0.33) bucketMid.push(dot);
        else bucketDim.push(dot);
      }

      // Three batched fills, brightness × presence × proximity-boost.
      // With 'lighter' composite these accumulate against whatever is below.
      const boost = 1 + 0.18 * proximity;
      const fillBucket = (arr: Dot[], alpha: number) => {
        if (arr.length === 0) return;
        const a = alpha * presence * boost;
        ctx.fillStyle = `rgba(${dotRGB.r},${dotRGB.g},${dotRGB.b},${a.toFixed(3)})`;
        ctx.beginPath();
        for (const dot of arr) {
          ctx.moveTo(dot.x + dot.r, dot.y);
          ctx.arc(dot.x, dot.y, dot.r, 0, Math.PI * 2);
        }
        ctx.fill();
      };
      fillBucket(bucketDim,    0.22);
      fillBucket(bucketMid,    0.48);
      fillBucket(bucketBright, 0.88);

      if (mintArcs.length > 0) {
        const a = 0.62 * presence * boost;
        ctx.fillStyle = `rgba(${tintRGB.r},${tintRGB.g},${tintRGB.b},${a.toFixed(3)})`;
        ctx.beginPath();
        for (const dot of mintArcs) {
          ctx.moveTo(dot.x + dot.r, dot.y);
          ctx.arc(dot.x, dot.y, dot.r, 0, Math.PI * 2);
        }
        ctx.fill();
      }

      // Reset compositor for any subsequent draws (defensive).
      ctx.globalCompositeOperation = 'source-over';

      // Mirror the painted frame into the sharp-overlay canvas. Same pixels;
      // the two canvases differ only in their CSS filter blur radius and the
      // sharp one's drifting mask. drawImage between same-size canvases is a
      // single GPU blit — orders of magnitude cheaper than re-running the
      // wing math twice.
      // Skip the mirror while loading — the sharp overlay is hidden during
      // intro/merge (its double-brightness would fight the crisp loader look).
      if (phaseRef.current === 'ambient' && sharpCtx && sharpCanvas && canvas) {
        sharpCtx.save();
        sharpCtx.setTransform(1, 0, 0, 1, 0, 0);
        sharpCtx.clearRect(0, 0, sharpCanvas.width, sharpCanvas.height);
        sharpCtx.drawImage(canvas, 0, 0);
        sharpCtx.restore();
      }
    }

    const tick = () => {
      if (!visible || !docVisible) {
        rafId = requestAnimationFrame(tick);
        return;
      }
      const now = performance.now();
      const dt = Math.min(0.05, (now - lastFrameMs) / 1000);
      lastFrameMs = now;

      // ── advance the fish along the Catmull-Rom spline ──
      const elapsed = (now - fish.segStart) / 1000;
      const u = clamp(elapsed / fish.segDuration, 0, 1);
      // Mild cubic-in-out on top of the spline softens the pace within each
      // segment without re-introducing dead-stops at endpoints (the spline
      // already gives continuous tangent across segments).
      const eased = cubicInOut(u);
      const prev = fish.pose;
      const sampled = sampleCR(eased);

      // Water current — a tiny low-frequency offset that doesn't move the
      // fish, just makes it look like the water is gently shifting it.
      // Two sums-of-sines per axis, different frequencies, never repeats.
      const ct = now * 0.001;
      sampled.x += Math.sin(ct * 0.7) * CURRENT_AMPLITUDE_PX
                 + Math.sin(ct * 1.7 + 1.3) * CURRENT_AMPLITUDE_PX * 0.5;
      sampled.y += Math.cos(ct * 0.6) * CURRENT_AMPLITUDE_PX
                 + Math.sin(ct * 1.9 + 0.7) * CURRENT_AMPLITUDE_PX * 0.5;

      const dx = sampled.x - prev.x, dy = sampled.y - prev.y;
      const instSpeed = dt > 0 ? Math.sqrt(dx * dx + dy * dy) / dt : 0;
      smoothedSpeed += (instSpeed - smoothedSpeed) * Math.min(1, dt * 4);
      fish.pose = sampled;
      if (u >= 1) advanceSegment(now);

      // ── light/dark mode: is the fish currently over a light section? ──
      // During intro/merge the backdrop is the teal loading cover, so the
      // fish is always in dark mode regardless of what's underneath.
      let lightTarget = 0;
      if (phaseRef.current === 'ambient') {
        const fishDocY = fish.pose.y + window.scrollY;
        for (const rg of lightRanges) {
          if (fishDocY >= rg.top && fishDocY <= rg.bottom) {
            lightTarget = 1;
            break;
          }
        }
      }
      lightMix += (lightTarget - lightMix) * Math.min(1, dt * 2.5);

      // ── wandering color: hold, then ease toward a fresh bright hue ──
      if (now >= nextColorSwitch) {
        colorTarget = randomBrightColor();
        nextColorSwitch =
          now + (COLOR_HOLD_MIN_SEC + Math.random() * (COLOR_HOLD_MAX_SEC - COLOR_HOLD_MIN_SEC)) * 1000;
      }
      const colorK = Math.min(1, dt * COLOR_LERP_RATE);
      wingColor.r += (colorTarget.r - wingColor.r) * colorK;
      wingColor.g += (colorTarget.g - wingColor.g) * colorK;
      wingColor.b += (colorTarget.b - wingColor.b) * colorK;

      // ── presence envelope (visibility waxes and wanes over ~30s) ──
      const presA = 0.5 + 0.5 * Math.sin(now * PRESENCE_FREQ_A);
      const presB = 0.5 + 0.5 * Math.sin(now * PRESENCE_FREQ_B + 1.3);
      const presenceNorm = presA * 0.6 + presB * 0.4; // 0..1
      // Loader phases pin presence to full — the creature is the show.
      const presence = phaseRef.current !== 'ambient'
        ? 1
        : PRESENCE_MIN + (PRESENCE_MAX - PRESENCE_MIN) * presenceNorm;

      // ── proximity to the fish (for wing-flap boost) ──
      const proximity = (() => {
        if (cursor.x < 0) return 0;
        const ddx = cursor.x - fish.pose.x, ddy = cursor.y - fish.pose.y;
        const dist = Math.sqrt(ddx * ddx + ddy * ddy);
        return Math.max(0, 1 - dist / PROXIMITY_RADIUS_PX);
      })();

      // ── advance wing math t ──
      const flapRate = BASE_T_RATE
        * (1 + PROXIMITY_BOOST_MAX * proximity)
        + smoothedSpeed * SPEED_TO_TRATE;
      tAccumulator += dt * flapRate + scrollKick;
      scrollKick *= SCROLL_DECAY;
      if (scrollKick < 0.0005) scrollKick = 0;

      paintField(tAccumulator, proximity, presence, now);

      // Drift the "clear patch" window across the viewport. Two slow sines
      // per axis (different periods → never repeats) keep the window from
      // tracing a perfect ellipse. Range stays inside the safe area so the
      // window never touches an edge. Setting a single style on a
      // compositor-promoted layer (the canvas) is cheap.
      if (sharpCanvas) {
        const mx = 50 + 28 * Math.sin(now * 0.000093) + 8 * Math.sin(now * 0.00021 + 1.7);
        const my = 50 + 22 * Math.cos(now * 0.000077) + 8 * Math.sin(now * 0.00027 + 0.4);
        const pos = `${mx.toFixed(2)}% ${my.toFixed(2)}%`;
        sharpCanvas.style.maskPosition = pos;
        sharpCanvas.style.webkitMaskPosition = pos;
      }

      rafId = requestAnimationFrame(tick);
    };

    if (reduced) {
      // No loading animation for reduced-motion users: drop the cover
      // immediately and (on desktop) show the still ambient frame. The
      // setState is deferred a tick (lint: no sync setState in effects);
      // the ref switches now so the paint below renders in ambient mode.
      phaseRef.current = skipAmbient ? 'off' : 'ambient';
      const reducedTimer = window.setTimeout(
        () => setPhase(phaseRef.current), 0);
      if (skipAmbient) return () => clearTimeout(reducedTimer);
      resize();
      measureLightSections();
      // Centre the fish, one paint, no animation. Full presence so it's
      // visible at all on the still frame.
      fish.pose = { x: viewportW * 0.62, y: viewportH * 0.46, scale: 1, rot: 0 };
      const fishDocY = fish.pose.y + window.scrollY;
      lightMix = lightRanges.some((rg) => fishDocY >= rg.top && fishDocY <= rg.bottom) ? 1 : 0;
      paintField(Math.PI * 0.75, 0, 1, performance.now());
      return () => clearTimeout(reducedTimer);
    }

    const io = new IntersectionObserver(
      (entries) => { for (const e of entries) visible = e.isIntersecting; },
      { threshold: 0.01 }
    );
    io.observe(container);

    resize();
    measureLightSections();
    // Slow refresh keeps the ranges honest across client-side navigations and
    // late layout shifts (pinned sections resize themselves after mount).
    const lightInterval = setInterval(measureLightSections, 2500);
    const onResizeAll = () => {
      resize();
      measureLightSections();
    };
    window.addEventListener('resize', onResizeAll);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseleave', onMouseLeave);
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('visibilitychange', onDocVisibility);

    rafId = requestAnimationFrame(tick);

    // ── intro → merge → ambient orchestration ──
    // Merge when the page has loaded AND the loader has shown for at least
    // INTRO_MIN_MS. A hard cap merges anyway if some straggling subresource
    // (e.g. the contact-page video) keeps the load event hostage.
    const introStart = performance.now();
    let merged = false;
    let stopped = false;
    let mergeTimer = 0;
    let capTimer = 0;
    let settleTimer = 0;

    function stopAll() {
      if (stopped) return;
      stopped = true;
      cancelAnimationFrame(rafId);
      clearInterval(lightInterval);
      io.disconnect();
      window.removeEventListener('resize', onResizeAll);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('load', queueMerge);
      document.removeEventListener('visibilitychange', onDocVisibility);
      clearTimeout(mergeTimer);
      clearTimeout(capTimer);
      clearTimeout(settleTimer);
    }

    function startMerge() {
      if (merged) return;
      merged = true;
      clearTimeout(capTimer);
      phaseRef.current = 'merge';
      setPhase('merge');
      // Once the dive completes: desktop settles into the ambient layer;
      // mobile fades out entirely and the loop shuts down (the always-on
      // layer stays disabled there for performance).
      settleTimer = window.setTimeout(() => {
        if (skipAmbient) {
          phaseRef.current = 'off';
          setPhase('off');
          stopAll();
        } else {
          phaseRef.current = 'ambient';
          setPhase('ambient');
        }
      }, MERGE_MS + 50);
    }

    function queueMerge() {
      const wait = Math.max(0, INTRO_MIN_MS - (performance.now() - introStart));
      mergeTimer = window.setTimeout(startMerge, wait);
    }

    if (document.readyState === 'complete') {
      queueMerge();
    } else {
      window.addEventListener('load', queueMerge, { once: true });
      capTimer = window.setTimeout(startMerge, INTRO_MAX_WAIT_MS);
    }

    return stopAll;
  }, [enabled]);

  if (!enabled || phase === 'off') return null;

  const isIntro = phase === 'intro';
  const isMerge = phase === 'merge';
  // intro/merge: the wing is the loading screen, floating above everything
  // on a solid teal cover. ambient: the background layer behind content.
  const overlayActive = isIntro || isMerge;
  const mergeEase = `cubic-bezier(0.4, 0, 0.2, 1)`;
  // Ambient blur 13px (was 22): enough frost that the fish reads as "behind
  // glass", light enough that its shape and motion stay clearly visible —
  // an aquarium fish, not a ghost.
  const baseBlur = 'blur(13px) saturate(120%)';
  const introBlur = 'blur(3px) saturate(130%)';

  return (
    <>
      {/* Loading cover: solid brand teal above the whole page while the wing
          performs. Rendered in the SSR HTML (phase starts as 'intro') so the
          first paint is the loader, not a flash of half-loaded page. It never
          intercepts pointer events, and a pure-CSS failsafe animation fades
          it out even if JS never runs (see globals.css). */}
      {overlayActive && (
        <div
          aria-hidden
          className="fixed inset-0 z-[9990] pointer-events-none"
          style={{
            backgroundColor: '#173835',
            opacity: isMerge ? 0 : 1,
            transition: `opacity ${MERGE_MS}ms ${mergeEase}`,
            animation: 'ambient-cover-failsafe 800ms ease 8s forwards',
          }}
        />
      )}
      {/* The wing layer. During the loader it sits just above the cover
          (z-9991); once merged it drops to z-[-1] between the GlobalBackground
          (z-[-2], deep teal + cursor spotlight) and the page content, so the
          creature reads as part of the backdrop — visible wherever sections
          let the background show through, never on top of text or UI. The
          merge is a "dive": blur ramps up while opacity falls to 0 in sync
          with the cover, then it re-emerges behind the content — clearly
          visible, swimming around like a fish in an aquarium.
          Always pointer-events-none. */}
      <div
        ref={containerRef}
        aria-hidden
        className="fixed inset-0 pointer-events-none overflow-hidden"
        // Ambient opacity 0.82: clearly visible swimming in the backdrop
        // (translucent section backgrounds still dim it a second time), while
        // staying behind all content so it never competes with text.
        style={{
          zIndex: overlayActive ? 9991 : -1,
          opacity: isIntro ? 1 : isMerge ? 0 : 0.82,
          transition: isMerge
            ? `opacity ${MERGE_MS}ms ${mergeEase}`
            : `opacity ${AMBIENT_FADE_MS}ms ease`,
        }}
      >
      {/* The aquarium pane: blur is applied to the CANVAS itself, not as a
          backdrop-filter on a pane above. A backdrop-filter forces the
          compositor to re-sample and re-blur the entire underlying scene
          every frame the canvas repaints — that was the source of the lag
          and the flicker. Blurring the canvas directly is a single GPU
          filter pass, and only the wing (the thing meant to look blurred)
          is affected. The dark teal background stays sharp.

          Two stacked canvases: the base is heavily blurred frosted glass.
          On top, the sharp-overlay canvas mirrors the same pixels at a
          lower blur radius, but masked to a drifting soft window — so a
          patch of the viewport reads as "thinner glass" and the fish is
          more visible there. The window's position is driven from the
          rAF loop (sin-based, never repeats). */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 h-full w-full"
        style={{
          // Near-sharp while it's the loader; frosted glass once ambient.
          filter: isIntro ? introBlur : baseBlur,
          WebkitFilter: isIntro ? introBlur : baseBlur,
          transition: `filter ${MERGE_MS}ms ${mergeEase}, -webkit-filter ${MERGE_MS}ms ${mergeEase}`,
        }}
      />
      <canvas
        ref={sharpCanvasRef}
        className="absolute inset-0 h-full w-full"
        style={{
          // Hidden during the loader (the base canvas is already near-sharp
          // there — mirroring it would double the brightness); fades in with
          // the ambient state.
          opacity: overlayActive ? 0 : 1,
          transition: `opacity ${AMBIENT_FADE_MS}ms ease`,
          // Moderate blur delta (13 ↔ 6) keeps the two layers' content from
          // visibly contrasting at the transition — no hard "sharp meets
          // very blurry" seam — while the clear patch reads noticeably
          // crisper, like a thin spot in the aquarium glass.
          filter: 'blur(6px) saturate(122%)',
          WebkitFilter: 'blur(6px) saturate(122%)',
          // Oversize the mask to 200% × 200% so the gradient extends well
          // past the viewport edges. Combined with stopping at alpha ~0.08
          // (not 0) at the gradient's far end, the mask never hits zero
          // anywhere on screen — eliminating the visible boundary. The
          // effect: a soft "thinner glass" zone that gently grades into
          // "thicker glass" with no detectable line.
          maskImage:
            'radial-gradient(circle at center, ' +
              'rgba(0,0,0,1.00)  0%, ' +
              'rgba(0,0,0,0.92) 12%, ' +
              'rgba(0,0,0,0.75) 24%, ' +
              'rgba(0,0,0,0.52) 36%, ' +
              'rgba(0,0,0,0.32) 50%, ' +
              'rgba(0,0,0,0.18) 65%, ' +
              'rgba(0,0,0,0.08) 85%, ' +
              'rgba(0,0,0,0.04) 100%)',
          WebkitMaskImage:
            'radial-gradient(circle at center, ' +
              'rgba(0,0,0,1.00)  0%, ' +
              'rgba(0,0,0,0.92) 12%, ' +
              'rgba(0,0,0,0.75) 24%, ' +
              'rgba(0,0,0,0.52) 36%, ' +
              'rgba(0,0,0,0.32) 50%, ' +
              'rgba(0,0,0,0.18) 65%, ' +
              'rgba(0,0,0,0.08) 85%, ' +
              'rgba(0,0,0,0.04) 100%)',
          maskSize: '200% 200%',
          WebkitMaskSize: '200% 200%',
          maskRepeat: 'no-repeat',
          WebkitMaskRepeat: 'no-repeat',
          maskPosition: '50% 50%',
          WebkitMaskPosition: '50% 50%',
        }}
      />
      </div>
    </>
  );
}

export default AmbientWing;
